import { useReactiveVar } from "@apollo/client";
import { router, usePathname } from "expo-router";
import { useCallback, useMemo } from "react";

import HeaderView from "./HeaderWatchListView";

import { useCurrentUser } from "@/features/auth";
import {
  UserWatchlistRole,
  useWatchlistListUserWatchlistsByWatchlistIdQuery,
  WatchlistWatchlistMetaFragment,
} from "@/generated/graphql";
import useMobileScreen from "@/hooks/useMobileScreen";
import { changeShareVar } from "@/stores/common";
import {
  focusSelectedVar,
  isFocusSearchInputVar,
} from "@/stores/isFocusSearchInput";
import { watchListSelectedVar } from "@/stores/player";
import { myRosterSelectedVar } from "@/stores/watchlistSelection";
import { getCognitoDisplayName } from "@/utils/getCognitoDisplayName";

/**
 *
 */
interface Props {
  loading?: boolean;
  watchlists?: WatchlistWatchlistMetaFragment[];
  selectedWatchlist?: WatchlistWatchlistMetaFragment | null;
  isShowPopoverFilter?: boolean;
  isShowHeaderSearch?: boolean;
  watchListVisible?: boolean;
  onCallbackOpen?: () => void;
  onSelectWatchlist?: (value: string) => void;
  onOpenMyRoster?: () => void;
}

/**
 * Container component for the watchlist header with search, filters, and collaboration features
 * @param root0 - Component props
 * @param root0.loading - Whether watchlist data is currently loading
 * @param root0.watchlists - Array of available watchlists for selection
 * @param root0.selectedWatchlist - Currently selected watchlist to display
 * @param root0.isShowPopoverFilter - Whether to show the filter popover in the header
 * @param root0.isShowHeaderSearch - Whether to display the search bar in the header
 * @param root0.watchListVisible - Whether to show the watchlist selector and collaboration UI
 * @param root0.onCallbackOpen - Callback invoked when user opens the watchlist menu
 * @param root0.onSelectWatchlist - Callback invoked when user selects a different watchlist with watchlist ID
 * @param root0.onOpenMyRoster - Callback invoked when user selects My Roster, overrides internal handler when provided
 */
const HeaderWatchListContainer = ({
  loading,
  watchlists,
  selectedWatchlist,
  isShowPopoverFilter,
  isShowHeaderSearch,
  watchListVisible,
  onCallbackOpen,
  onSelectWatchlist,
  onOpenMyRoster: onOpenMyRosterProp,
}: Props) => {
  const pathName = usePathname();
  const isSmallScreen = useMobileScreen();
  const isSearch = pathName === "/player-search";
  const focusValue = useReactiveVar(focusSelectedVar);
  const currentUser = useCurrentUser();
  const handleSearchFocus = useCallback(() => {
    if (isSmallScreen) {
      router.push("/player-search");
      setTimeout(() => {
        isFocusSearchInputVar(true);
        focusSelectedVar({ type: "search", isShow: true });
      }, 300);
    }
  }, [isSmallScreen]);

  const handleSelectWatchList = useCallback(
    (watchListId: string) => {
      watchListSelectedVar(watchListId);
      myRosterSelectedVar(false);
      if (pathName.includes("players")) {
        router.push(`/players?watchlistId=${watchListId}`);
      }
    },
    [pathName]
  );

  const handleOpenMyRoster = useCallback(() => {
    // Always set myRosterSelectedVar to true when opening My Roster
    // This ensures the roster displays correctly after switching from a watchlist
    myRosterSelectedVar(true);
    watchListSelectedVar(undefined);
    if (!isSmallScreen) {
      router.push("/players");
    }
  }, [isSmallScreen]);

  const handleClickableShare = useCallback(() => changeShareVar(true), []);
  const { data } = useWatchlistListUserWatchlistsByWatchlistIdQuery({
    // NOTE: load them all so we can check it a user is on the watchlist. With scale, we'll need to move this to a dataresolver
    variables: { watchlistId: selectedWatchlist?.id || "", first: 100 },
    skip: !selectedWatchlist?.id,
  });

  const userWatchLists = useMemo(
    () =>
      (data?.listUserWatchlistsByWatchlistId?.edges ?? []).map(
        edge => edge?.node
      ),
    [data?.listUserWatchlistsByWatchlistId?.edges]
  );

  const watchlistOwner = useMemo(
    () =>
      userWatchLists.find(
        userWatchlist => userWatchlist?.role === UserWatchlistRole.Owner
      )?.user,
    [userWatchLists]
  );
  const isMyWatchlist = watchlistOwner?.id === currentUser?.id;
  // SE-6562: both Cognito name attributes are optional, so interpolating them
  // unguarded rendered "undefined undefined and N others" in the header.
  const watchListOwnerName = getCognitoDisplayName(
    watchlistOwner?.cognitoUser?.Attributes,
    "Unknown User"
  );

  const collaborators = useMemo(
    () =>
      userWatchLists
        .filter(userWatchlist =>
          !isMyWatchlist ? true : userWatchlist?.user?.id !== currentUser?.id
        )
        .filter(userWatchlist => !!userWatchlist?.user.title)
        .map(userWatchlist => userWatchlist?.user),
    [currentUser?.id, isMyWatchlist, userWatchLists]
  );
  const collaboratorCount = collaborators.length - (!isMyWatchlist ? 1 : 0);

  return (
    <HeaderView
      loading={loading}
      collaborators={collaborators}
      collaboratorCount={collaboratorCount}
      selectedWatchlist={selectedWatchlist}
      watchlists={watchlists}
      isShowPopoverFilter={isShowPopoverFilter}
      isShowHeaderSearch={isShowHeaderSearch}
      onSearchFocus={isSearch ? undefined : handleSearchFocus}
      onSelectWatchlist={onSelectWatchlist || handleSelectWatchList}
      onOpenMyRoster={onOpenMyRosterProp || handleOpenMyRoster}
      onCallbackOpen={onCallbackOpen}
      onClickableShare={handleClickableShare}
      isShowWatchList={watchListVisible ?? focusValue.type === "default"}
      isMyWatchList={isMyWatchlist}
      watchListOwnerName={watchListOwnerName}
      userWatchLists={userWatchLists}
      isSmallScreen={isSmallScreen}
    />
  );
};

export default HeaderWatchListContainer;
